import type { GfxGroupLikeElementModel } from '@blocksuite/block-std/gfx';
import { GfxBlockElementModel as GfxBlockModel } from '@blocksuite/block-std/gfx';
import type {
  EdgelessRootBlockSpecType,
  PageRootBlockSpecType,
  RootService,
  SurfaceRefBlockModel,
  TelemetryEventMap,
} from '@blocksuite/blocks';
import {
  EdgelessRootService,
  MarkdownAdapter,
  NoteDisplayMode,
  PageRootService,
} from '@blocksuite/blocks';
import type { Disposable } from '@blocksuite/global/utils';
import { Bound, debounce } from '@blocksuite/global/utils';
import type { BlockModel, Text } from '@blocksuite/store';

interface RootBlockModelWithTitle extends BlockModel {
  title: Text;
}
import { Job, Slice } from '@blocksuite/store';
import {
  AIEdgelessRootBlockSpec,
  AIPageRootBlockSpec,
  AIProvider,
} from '@pique/core/blocksuite/presets/ai';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { posthog } from '@pique/core/posthog';
import { type FrameworkProvider } from '@toeverything/infra';

import { createLinkedWidgetConfig } from './linked-widget';
import { PiqueMindmapElementModel } from './pique/mindmap-element';
import { paragraphBlockAddedSlot, pauseMediaSlot } from './pique/pique-slots';

/* -------------------------------------------------------------------------------------------------
 * Types and Enums
 * ------------------------------------------------------------------------------------------------- */

export interface ElementUpdatedData {
  id: string;
  props: Record<string, unknown>;
  oldValues: Record<string, unknown>;
  local: boolean;
}

export enum NoteShadow {
  Box = '--affine-note-shadow-box',
  Film = '--affine-note-shadow-film',
  Float = '--affine-note-shadow-float',
  None = '',
  Paper = '--affine-note-shadow-paper',
  Sticker = '--affine-note-shadow-sticker',
}

export enum NoteBackgroundColor {
  Black = '--affine-note-background-black',
  Blue = '--affine-note-background-blue',
  Green = '--affine-note-background-green',
  Grey = '--affine-note-background-grey',
  Magenta = '--affine-note-background-magenta',
  Orange = '--affine-note-background-orange',
  Purple = '--affine-note-background-purple',
  Red = '--affine-note-background-red',
  Teal = '--affine-note-background-teal',
  White = '--affine-note-background-white',
  Yellow = '--affine-note-background-yellow',
}

const COLLAPSED_NOTE_HEIGHT = 50;

export const PIQUE_BLOCK_FLAVORS = [
  'affine:embed-pique-youtube',
  'affine:embed-pique-quiz',
  'affine:embed-pique-html',
] as const;

export interface BlockContentMap {
  blockId: string;
  text: string;
  type: 'note' | 'surface';
}

export interface PageContent {
  fullText: string;
  blocks: BlockContentMap[];
}

interface EdgelessProps {
  scale?: number;
  collapsedHeight?: number;
  collapse?: boolean;
  style?: {
    shadowType?: string;
    collapsedHeight?: number;
    collapse?: boolean;
  };
}

interface EdgelessBlockModel extends BlockModel {
  edgeless: EdgelessProps;
  model: BlockModel;
  displayMode?: NoteDisplayMode;
  xywh?: string;
}

function isEdgelessBlockModel(model: BlockModel): model is EdgelessBlockModel {
  if (!model) return false;
  if (!(model instanceof GfxBlockModel)) return false;
  if (!('xywh' in model)) return false;
  return true;
}

/* -------------------------------------------------------------------------------------------------
 * Google Fonts
 * ------------------------------------------------------------------------------------------------- */

const GOOGLE_FONTS = [
  {
    font: 'Inter',
    name: 'Inter',
    url: 'https://fonts.gstatic.com/s/inter/v12/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa1ZL7.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Inter',
    name: 'Inter',
    url: 'https://fonts.gstatic.com/s/inter/v12/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa2JL7SUc.woff2',
    weight: '500',
    style: 'normal',
  },
  {
    font: 'Inter',
    name: 'Inter',
    url: 'https://fonts.gstatic.com/s/inter/v12/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa2pL7SUc.woff2',
    weight: '600',
    style: 'normal',
  },
  {
    font: 'Inter',
    name: 'Inter',
    url: 'https://fonts.gstatic.com/s/inter/v12/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa1pL7SUc.woff2',
    weight: '700',
    style: 'normal',
  },
  {
    font: 'Outfit',
    name: 'Outfit',
    url: 'https://fonts.gstatic.com/s/outfit/v11/QGYvz_MVcBeNP4NJtEtqUYLknw.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Outfit',
    name: 'Outfit',
    url: 'https://fonts.gstatic.com/s/outfit/v11/QGYvz_MVcBeNP4NJtEtqUYLknw.woff2',
    weight: '500',
    style: 'normal',
  },
  {
    font: 'Outfit',
    name: 'Outfit',
    url: 'https://fonts.gstatic.com/s/outfit/v11/QGYvz_MVcBeNP4NJtEtqUYLknw.woff2',
    weight: '600',
    style: 'normal',
  },
  {
    font: 'Poppins',
    name: 'Poppins',
    url: 'https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrJJfecg.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Lora',
    name: 'Lora',
    url: 'https://fonts.gstatic.com/s/lora/v32/0QI6MX1D_JOuGQbT0gvTJPa787weuxJBkq0.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Roboto',
    name: 'Roboto',
    url: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Open Sans',
    name: 'Open Sans',
    url: 'https://fonts.gstatic.com/s/opensans/v35/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-muw.woff2',
    weight: '400',
    style: 'normal',
  },
  {
    font: 'Playfair Display',
    name: 'Playfair Display',
    url: 'https://fonts.gstatic.com/s/playfairdisplay/v36/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDXbtM.woff2',
    weight: '400',
    style: 'normal',
  },
];

let hasLoadedGoogleFonts = false;

function customLoadFonts(service: RootService): void {
  if (hasLoadedGoogleFonts) return;
  hasLoadedGoogleFonts = true;
  service.fontLoader.load(GOOGLE_FONTS);
}

function withAffineRootService(
  Service: typeof PageRootService,
  workbenchService?: WorkbenchService
) {
  return class extends Service {
    private _workbenchService: WorkbenchService | undefined = workbenchService;
    private readonly _hiddenElements: Set<string> = new Set();

    private _fullViewportBlockId: string | null = null;

    private _viewportChangeListener: Disposable | null = null;
    private _blockUpdatedListener: Disposable | null = null;
    private _elementUpdatedListener: Disposable | null = null;
    private _hasInitializedViewport = false;
    private _animationQueue: ReturnType<typeof setTimeout>[] = [];

    override setWorkbenchService(workbenchService: WorkbenchService) {
      this._workbenchService = workbenchService;
    }

    override getWorkbenchService(): WorkbenchService | undefined {
      return this._workbenchService;
    }

    override toggleChatPanel(visible?: boolean) {
      this._workbenchService?.workbench.toggleChatPanel(visible);
    }

    override setKeyboardMode(mode: 'page' | 'edgeless' | null) {
      const rootElement = this.host?.querySelector(
        ':scope > affine-page-root, :scope > affine-edgeless-root'
      ) as any;

      if (rootElement?.keyboardManager) {
        rootElement.keyboardManager.activeViewMode = mode;
      }
    }

    openSplitPanel() {
      this._workbenchService?.workbench.collapseLeftSidebar();
      this._workbenchService?.workbench.openCurrentDocBeside();
    }

    override setFirstViewToLarge(
      firstViewSize?: number,
      secondViewSize?: number
    ) {
      this._workbenchService?.workbench.setFirstViewToLarge(
        firstViewSize,
        secondViewSize
      );

      if (environment.isTauri) {
        import('../../../../../utils/window-utils')
          .then(({ restoreWindow }) => {
            restoreWindow().catch(error => {
              console.error(
                'Failed to restore window to normal size and position:',
                error
              );
            });
          })
          .catch(error => {
            console.error('Failed to import window utils:', error);
          });
      }
    }

    override openSidebar(): void {
      this._workbenchService?.workbench.openSidebar();
      this._workbenchService?.workbench.activeView$.value?.activeSidebarTab(
        'edgeless'
      );
      this._handleViewportChange();
    }

    override currentDocTags(): string[] {
      if (!this.doc?.collection?.meta || !this.doc.id) {
        return [];
      }
      return this.doc.collection.meta.getDocMeta(this.doc.id)?.tags ?? [];
    }

    override currentDocTitle(): string {
      return this.doc.meta?.title ?? '';
    }

    override setDocTitle(title: string): void {
      if (!this.doc) return;

      const page = this.doc;
      const pageBlock = page.root as RootBlockModelWithTitle | null;
      if (!pageBlock) return;

      page.transact(() => {
        pageBlock.title.delete(0, pageBlock.title.length);
        pageBlock.title.insert(title, 0);
      });

      page.collection.meta.setDocMeta(page.id, { title });
    }

    override setDocTitleFromString(title: string): void {
      // Create a segmenter for word segmentation
      const segmenter = new Intl.Segmenter('en', { granularity: 'word' });
      // Get first 5 word segments
      const firstFiveWords = Array.from(segmenter.segment(title))
        .filter(segment => segment.isWordLike)
        .slice(0, 5)
        .map(segment => segment.segment)
        .join(' ');
      this._animateDocTitleChange(firstFiveWords + '...').catch(error => {
        console.error('Error animating doc title:', error);
      });
    }

    override setDocTitleFromStringNoTruncate(title: string): void {
      const currentTitle = this.currentDocTitle();
      const trimmed = (
        typeof currentTitle === 'string'
          ? currentTitle
          : String(currentTitle ?? '')
      ).trim();
      const normalizedTitle = trimmed.toLowerCase();
      const shouldReplace =
        !trimmed || normalizedTitle === 'untitled' || normalizedTitle === 'title';

      if (shouldReplace) {
        this._animateDocTitleChange(title).catch(error => {
          console.error('Error animating exact doc title:', error);
        });
      }
    }

    private _titleAnimationId: number | null = null;

    private _animateDocTitleChange(newTitle: string): Promise<void> {
      return new Promise<void>(resolve => {
        const docPage = document.querySelector('.doc-title-container');
        if (!docPage) {
          resolve();
          return;
        }

        const richText = docPage.querySelector('rich-text') as HTMLElement & {
          inlineEditor?: {
            setText: (text: string) => void;
            insertText: (
              range: { index: number; length: number },
              text: string
            ) => void;
          };
        };
        if (!richText?.inlineEditor) {
          resolve();
          return;
        }

        if (this._titleAnimationId !== null) {
          cancelAnimationFrame(this._titleAnimationId);
          this._titleAnimationId = null;
        }

        const editor = richText.inlineEditor;
        editor.setText('');

        const CHARS_PER_TICK = 1;
        const MS_PER_CHAR = 12;
        let charIndex = 0;
        let nextTime = performance.now();

        const tick = (time: number) => {
          if (charIndex >= newTitle.length) {
            this._titleAnimationId = null;
            resolve();
            return;
          }

          if (time >= nextTime) {
            const end = Math.min(charIndex + CHARS_PER_TICK, newTitle.length);
            const chunk = newTitle.slice(charIndex, end);
            if (chunk) {
              editor.insertText({ index: charIndex, length: 0 }, chunk);
            }
            charIndex = end;
            nextTime = time + MS_PER_CHAR;
          }

          this._titleAnimationId = requestAnimationFrame(tick);
        };

        this._titleAnimationId = requestAnimationFrame(tick);
      });
    }

    override loadFonts(): void {
      customLoadFonts(this);
    }

    override telemetryService = {
      track: <T extends keyof TelemetryEventMap>(
        eventName: T,
        props: TelemetryEventMap[T]
      ) => {
        posthog.track(eventName as string, props as Record<string, unknown>);
      },
    };

    override getSidebarEdgelessRootService(): Promise<
      EdgelessRootService | undefined
    > {
      return new Promise(resolve => {
        this.openSplitPanel();
        let attempts = 0;
        const maxAttempts = 5;
        const tryFindEdgelessRoot = () => {
          const edgelessRoot = document.querySelector(
            'affine-edgeless-root'
          ) as
            | (HTMLElement & {
                host?: {
                  std?: {
                    spec?: {
                      getService?: (name: string) => unknown;
                    };
                  };
                };
              })
            | null;

          if (edgelessRoot?.host?.std?.spec?.getService) {
            try {
              const service =
                edgelessRoot.host.std.spec.getService('affine:page');
              if (service) {
                resolve(service as EdgelessRootService);
              } else {
                resolve(undefined);
              }
            } catch (error) {
              console.warn('Failed to get EdgelessRootService:', error);
              resolve(undefined);
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(tryFindEdgelessRoot, 100);
          } else {
            resolve(undefined);
          }
        };
        tryFindEdgelessRoot();
      });
    }

    override hideOthersAndAddBlock(
      flavour: string,
      props: Record<string, unknown>,
      parent?: string | BlockModel,
      parentIndex?: number
    ) {
      this.hideAllElements();
      return (this as unknown as EdgelessRootService).addBlock(
        flavour,
        props,
        parent,
        parentIndex
      );
    }

    override hideOthersAndAddElement<T extends Record<string, unknown>>(
      type: string,
      props: T
    ): string {
      this.hideAllElements();
      return (this as unknown as EdgelessRootService).addElement(type, props);
    }

    override hideAllElements(): void {
      // this.doc.addBlock('pique:edgeless-element-tab', {}, this.doc.root?.id);
      const edgelessService = this as unknown as EdgelessRootService;
      edgelessService.elements.slice().forEach(element => {
        edgelessService.removeElement(element);
      });
    }

    override selectAllBlocks() {
      if (!this.doc?.root || !this.std?.selection) {
        console.warn('[root block] Document root or selection not available');
        return;
      }
      const allBlocks = this.doc.root.children ?? [];
      const blockSelections = allBlocks.map(block =>
        this.std.selection.create('block', { blockId: block.id })
      );
      this.std.selection.setGroup('note', blockSelections);
    }

    override addFullViewportBlock(): string {
      const edgelessService = this as unknown as EdgelessRootService;
      const { width, height } = edgelessService.viewport;
      const bound = new Bound(0, 0, width, height / 2);

      const blockId = this.hideOthersAndAddBlock(
        'affine:note',
        {
          xywh: bound.serialize(),
          background: 'transparent',
          displayMode: NoteDisplayMode.EdgelessOnly,
          edgeless: {
            style: {
              shadowType: NoteShadow.None,
              collapsedHeight: COLLAPSED_NOTE_HEIGHT,
              collapse: false,
            },
          },
        },
        this.doc?.root?.id
      );

      this._fullViewportBlockId = blockId;
      this._initializeFullViewportBlock();
      return blockId;
    }

    private _initializeFullViewportBlock(): void {
      this._setEdgelessServiceFullViewportBound();
      this._setupViewportChangeListener();
      this._resizeFullViewportBlock();
    }

    private _setEdgelessServiceFullViewportBound(): void {
      const edgelessService = this as unknown as EdgelessRootService;
      const viewport = edgelessService?.viewport;
      if (!viewport) return;
      const { width, height } = viewport;
      if (!width || !height) return;
      const bound = new Bound(0, 0, width, height / 2);
      edgelessService.viewport.setViewportByBound(bound, [0, 0, 0, 0], true);
    }

    private _setupViewportChangeListener(): void {
      this._removeViewportChangeListener();
      const edgelessService = this as unknown as EdgelessRootService;
      if (!edgelessService.viewport) return;
      this._viewportChangeListener =
        edgelessService.viewport.viewportUpdated.on(
          this._handleViewportChange.bind(this)
        );
    }

    private _handleViewportChange(): void {
      if (!this._fullViewportBlockId) {
        this._findFullViewportBlock();
      }
      this._resizeFullViewportBlock();
    }

    private _findFullViewportBlock(): void {
      if (this._hasInitializedViewport) return;

      if (!this.doc) {
        console.warn(
          '[root block] Document not available for finding viewport block'
        );
        return;
      }

      const doc = this.doc;
      let foundBlock = null;

      for (const flavor of PIQUE_BLOCK_FLAVORS) {
        const blocks = doc.getBlocksByFlavour(flavor);
        if (blocks.length > 0) {
          foundBlock = blocks[0];
          break;
        }
      }

      if (!foundBlock) {
        this._fullViewportBlockId = null;
        return;
      }

      const parentBlock = doc.getParent(foundBlock.id);
      if (!parentBlock || !isEdgelessBlockModel(parentBlock)) {
        console.warn(
          `Full viewport block ${foundBlock.id} parent is not edgeless; skipping.`
        );
        this._fullViewportBlockId = null;
        return;
      }

      this._fullViewportBlockId = parentBlock.id;
      this._initializeFullViewportBlock();
      this._hasInitializedViewport = true;
    }

    /**
     * Updated resizing logic using doc.transact() and correct scale/bound updates.
     */
    private _resizeFullViewportBlock(): void {
      if (!this._fullViewportBlockId) return;

      if (!this.doc) {
        console.warn('[root block] Document not available for resize');
        return;
      }

      const block = this.doc.getBlock(this._fullViewportBlockId);
      if (!block) return;
      const blockModel = block.model;

      const edgelessService = this as unknown as EdgelessRootService;
      if (!edgelessService?.viewport?.viewportBounds) return;
      const { viewportBounds, zoom } = edgelessService.viewport;
      const customViewportHeight = viewportBounds.w * 0.9;
      const topPadding = Math.max(28, viewportBounds.h * 0.02);

      if (isEdgelessBlockModel(blockModel)) {
        const currentCollapseState = blockModel.edgeless.collapse ?? false;
        const inverseZoom = 1 / zoom;
        const fullViewportBound = new Bound(
          viewportBounds.x,
          viewportBounds.y + topPadding,
          viewportBounds.w,
          currentCollapseState
            ? (blockModel.edgeless.collapsedHeight ?? COLLAPSED_NOTE_HEIGHT)
            : customViewportHeight
        );

        try {
          this.doc.transact(() => {
            edgelessService.updateElement(block.id, {
              xywh: fullViewportBound.serialize(),
              edgeless: {
                ...blockModel.edgeless,
                scale: inverseZoom,
                collapsedHeight: COLLAPSED_NOTE_HEIGHT,
                collapse: currentCollapseState,
              },
            });
          });

          const fullViewportBlockElement = edgelessService.getElementById(
            block.id
          );
          if (
            fullViewportBlockElement &&
            edgelessService.layer &&
            fullViewportBlockElement instanceof GfxBlockModel
          ) {
            try {
              edgelessService.reorderElement(fullViewportBlockElement, 'back');
            } catch (error) {
              console.warn('Failed to reorder element:', error);
            }
          }
        } catch (error) {
          console.error('Error resizing full viewport block:', error);
          this._fullViewportBlockId = null;
          this._removeViewportChangeListener();
        }
      }
    }

    private _removeViewportChangeListener(): void {
      if (this._viewportChangeListener) {
        this._viewportChangeListener.dispose();
        this._viewportChangeListener = null;
      }
    }

    override selectAllElements(): void {
      const edgelessService = this as unknown as EdgelessRootService;
      const allElements = [
        ...edgelessService.elements,
        ...edgelessService.blocks,
      ].map(el => el.id);
      edgelessService.selection.set({
        elements: allElements,
        editing: false,
      });
    }

    override hideToolbar(): void {
      setTimeout(() => {
        if (!this.host) {
          console.warn('[root block] Host not available');
          return;
        }
        const toolbar = this.host.querySelector(
          'edgeless-toolbar'
        ) as HTMLElement | null;
        if (toolbar) {
          toolbar.style.display = 'none';
        }
      });
    }

    override showToolbar(): void {
      if (!this.host) {
        console.warn('[root block] Host not available');
        return;
      }
      const toolbar = this.host.querySelector(
        'edgeless-toolbar'
      ) as HTMLElement | null;
      if (toolbar) {
        toolbar.style.bottom = '100px';
        toolbar.style.display = '';
      }
    }

    private _handleBlockUpdated(
      event:
        | {
            type: 'add';
            id: string;
            init: boolean;
            flavour: string;
            model: BlockModel;
          }
        | {
            type: 'delete';
            id: string;
            flavour: string;
            parent: string;
            model: BlockModel;
          }
        | {
            type: 'update';
            id: string;
            flavour: string;
            props: { key: string };
          }
    ): void {
      if (event.type === 'delete' && event.id === this._fullViewportBlockId) {
        this._fullViewportBlockId = null;
        this._removeViewportChangeListener();
      }

      // TEMPORARILY DISABLED: Auto title/tags generation on block add
      // To re-enable, use utils from './auto-title-generation':
      // if (event.type === 'add') {
      //   handleBlockAddedForTitleGeneration(this, state, debouncedGenerate, event);
      // }

      // Pique Update - Prevent creation of edgeless note blocks when a paragraph block is added
      if (event.type === 'add' && event.flavour === 'affine:paragraph') {
        const edgelessService = this as unknown as EdgelessRootService;

        if (!edgelessService.doc) {
          console.warn(
            '[edgeless root block] Document not available in service'
          );
          return;
        }

        const paragraphBlock = edgelessService.doc.getBlock(event.id);
        if (paragraphBlock?.model) {
          const paragraphBlockModel = paragraphBlock.model;
          const parentBlockModel =
            edgelessService.doc.getParent(paragraphBlockModel);

          if (
            parentBlockModel &&
            parentBlockModel.flavour === 'affine:note' &&
            (parentBlockModel as unknown as { displayMode?: NoteDisplayMode })
              .displayMode !== NoteDisplayMode.EdgelessOnly
          ) {
            // 1. Update the parent block to be doc only
            edgelessService.doc.updateBlock(parentBlockModel, {
              displayMode: NoteDisplayMode.DocOnly,
            });

            // 2. Emit an event to notify the paragraph block added
            paragraphBlockAddedSlot.emit(event.id);
          }
        }
      }
    }

    private _handleElementUpdated(event: ElementUpdatedData) {
      const { id } = event;
      const edgelessService = this as unknown as EdgelessRootService;

      if (!edgelessService.surface) {
        console.warn('[edgeless root block] Surface not available');
        return;
      }

      const updatedElementsPartOfGroups = edgelessService.surface.getGroups(id);
      if (updatedElementsPartOfGroups.length === 0) {
        return;
      }
      this._updateSurfaceRefComponentsDebounced(updatedElementsPartOfGroups);
    }

    private readonly _updateSurfaceRefComponentsDebounced = debounce(
      (updatedElementsPartOfGroups: GfxGroupLikeElementModel[]) => {
        this._updateSurfaceRefComponents(updatedElementsPartOfGroups);
      },
      300
    ) as ((updatedElementsPartOfGroups: GfxGroupLikeElementModel[]) => void) & {
      cancel?: () => void;
    };

    private _updateSurfaceRefComponents(
      updatedGroups: GfxGroupLikeElementModel[]
    ) {
      const groupIds = updatedGroups.map(group => group.id);

      const surfaceRefBlocks =
        this.doc.getBlocksByFlavour('affine:surface-ref');
      surfaceRefBlocks.forEach(block => {
        const blockModel = block.model as SurfaceRefBlockModel;
        if (groupIds.includes(blockModel.reference)) {
          const flavour = blockModel.flavour;
          const currentProps: Record<string, unknown> = {
            reference: blockModel.reference,
            caption: blockModel.caption,
            refFlavour: blockModel.refFlavour,
          };
          const maybeXywh = (blockModel as unknown as { xywh?: string }).xywh;
          if (maybeXywh) {
            currentProps['xywh'] = maybeXywh;
          }
          const parent = this.doc.getParent(blockModel);
          if (parent) {
            const index = parent.children.indexOf(blockModel);
            this.doc.deleteBlock(blockModel);
            this.doc.addBlock(flavour, currentProps, parent, index);
          }
        }
      });
    }

    override setViewportWithBoundAtBottom(
      bound: Bound,
      padding: [number, number, number, number] = [0, 0, 0, 0],
      desiredPaddingFactor: number = 0.2,
      smooth: boolean = true
    ) {
      const edgelessService = this as unknown as EdgelessRootService;
      const viewport = edgelessService.viewport;
      if (!viewport) return;
      const [pt, pr, pb, pl] = padding;
      const { width, height } = viewport;
      const zoom = Math.min(
        (width - (pr + pl)) / bound.w,
        (height - (pt + pb)) / bound.h
      );
      const centerX = bound.x + (bound.w + pr / zoom) / 2 - pl / zoom / 2;
      const desiredPadding = height * desiredPaddingFactor;
      const centerY =
        bound.y + bound.h - height / 2 / zoom + desiredPadding / zoom;

      viewport.setViewport(zoom, [centerX, centerY], smooth);
      pauseMediaSlot.emit();
    }

    override async getPageContentAsMarkdown(): Promise<string> {
      if (!this.doc?.collection) {
        console.warn('[root block] Document collection not available');
        return '';
      }
      const job = new Job({ collection: this.doc.collection });
      if (this.doc.root) {
        const currentSlice = Slice.fromModels(this.doc, [this.doc.root]);
        const original = await job.sliceToSnapshot(currentSlice);
        if (!original) {
          return '';
        }
        const adapter = new MarkdownAdapter(job);
        const markdown = await adapter.fromSliceSnapshot({
          snapshot: original,
          assets: job.assetsManager,
        });
        return markdown.file;
      }
      return '';
    }

    /**
     * Extracts all text content from the current page, including both doc and edgeless content.
     * @returns A promise that resolves to a PageContent object containing all text content and block mappings
     */
    override async getAllPageContent(): Promise<PageContent> {
      // Get all note blocks
      const noteBlocks = this.doc.getBlocksByFlavour('affine:note');
      const blockContents: BlockContentMap[] = [];

      // Get text content from each note block
      const textContents = await Promise.all(
        noteBlocks.map(async noteBlock => {
          const textBlocks = noteBlock.model.children.filter(
            child =>
              child.flavour === 'affine:paragraph' ||
              child.flavour === 'affine:list'
          );

          // Extract text from each text block
          const blockTexts = textBlocks.map(block => {
            if (!block.text) return '';
            const text = block.text.toString();
            if (text.trim()) {
              blockContents.push({
                blockId: block.id,
                text,
                type: 'note',
              });
            }
            return text;
          });

          return blockTexts.join('\n');
        })
      );

      // Get surface elements that contain text (like shapes with text)
      const surfaceBlocks = this.doc.getBlocksByFlavour('affine:surface');
      const surfaceTexts = surfaceBlocks.map(surfaceBlock => {
        const surfaceBlockModel = surfaceBlock.model;
        const textBlocks = surfaceBlockModel.children.filter(
          child =>
            child.flavour === 'affine:paragraph' ||
            child.flavour === 'affine:list'
        );

        const texts = textBlocks.map(block => {
          const text = block.text?.toString() || '';
          if (text.trim()) {
            blockContents.push({
              blockId: block.id,
              text,
              type: 'surface',
            });
          }
          return text;
        });

        return texts.join('\n');
      });

      // Combine all text content
      const fullText = [...textContents, ...surfaceTexts]
        .filter(Boolean)
        .join('\n\n')
        .trim();

      return {
        fullText,
        blocks: blockContents,
      };
    }

    override async askCopilotOneOff(
      prompt: string,
      options?: {
        schema?: object;
        model?: string;
        temperature?: number;
      }
    ): Promise<string> {
      try {
        if (!prompt || typeof prompt !== 'string') {
          throw new Error('Invalid prompt: Must be a non-empty string');
        }

        // Use AI provider infrastructure instead of direct API call
        if (!AIProvider.actions.oneOffRequest) {
          throw new Error('oneOffRequest action is not available');
        }

        const response = AIProvider.actions.oneOffRequest({
          content: prompt,
          workspaceId: this.doc.collection.id,
          docId: this.doc.id,
          host: this.host,
          stream: true,
          temperature: options?.temperature ?? 0,
          control: 'format-bar',
          where: 'ai-panel',
          params: {
            ...(options?.model && { model: options.model }),
            ...(options?.schema && { schema: JSON.stringify(options.schema) }),
          },
        });

        if (
          response &&
          typeof response === 'object' &&
          Symbol.asyncIterator in response
        ) {
          let fullResponse = '';
          for await (const chunk of response) {
            if (typeof chunk === 'string') {
              fullResponse += chunk;
            }
          }
          return fullResponse;
        }

        return response as string;
      } catch (error) {
        console.error(
          'Failed to get response from Copilot via AI provider:',
          error
        );
        throw error;
      }
    }

    override async *askCopilotOneOffStream(
      prompt: string,
      options?: {
        schema?: object;
        model?: string;
        temperature?: number;
      }
    ): AsyncIterable<string> {
      if (!AIProvider.actions.oneOffRequest) {
        throw new Error('oneOffRequest action is not available');
      }

      const stream = AIProvider.actions.oneOffRequest({
        content: prompt,
        workspaceId: this.doc.collection.id,
        docId: this.doc.id,
        host: this.host,
        stream: true,
        temperature: options?.temperature ?? 0,
        control: 'format-bar',
        where: 'ai-panel',
        params: {
          ...(options?.model && { model: options.model }),
          ...(options?.schema && { schema: JSON.stringify(options.schema) }),
        },
      });

      if (stream && Symbol.asyncIterator in stream) {
        yield* stream;
      }
    }

    override mounted(): void {
      super.mounted();

      this.openSplitPanel();

      this.hideToolbar();

      const edgelessService = this as unknown as EdgelessRootService;

      if (this.doc?.slots?.blockUpdated) {
        this._blockUpdatedListener = this.doc.slots.blockUpdated.on(
          this._handleBlockUpdated.bind(this)
        );
      }
      this._elementUpdatedListener =
        edgelessService.surface?.elementUpdated.on(
          this._handleElementUpdated.bind(this)
        ) ?? null;

      this._animationQueue.push(
        setTimeout(() => {
          try {
            if (!edgelessService?.edgelessElements) {
              return;
            }

            const mindmapElements = (
              edgelessService.edgelessElements as Array<{ group?: unknown }>
            ).filter(
              (element: { group?: unknown }) =>
                element.group instanceof PiqueMindmapElementModel
            );

            if (mindmapElements.length > 0) {
              const mindmap = mindmapElements[0]
                ?.group as PiqueMindmapElementModel;
              this._animationQueue.push(
                setTimeout(() => {
                  mindmap.zoomToVisible();
                }, 100)
              );
            }
          } catch {
            // ignore
          }
        }, 500)
      );
    }

    override unmounted(): void {
      super.unmounted();
      this._removeViewportChangeListener();

      if (this._blockUpdatedListener) {
        this._blockUpdatedListener.dispose();
        this._blockUpdatedListener = null;
      }

      if (this._elementUpdatedListener) {
        this._elementUpdatedListener.dispose();
        this._elementUpdatedListener = null;
      }

      if (this._animationQueue.length > 0) {
        this._animationQueue.forEach(clearTimeout);
        this._animationQueue = [];
      }

      if (this._titleAnimationId !== null) {
        cancelAnimationFrame(this._titleAnimationId);
        this._titleAnimationId = null;
      }

      this._updateSurfaceRefComponentsDebounced.cancel?.();
    }

    override hideElement(elementId: string): void {
      const blockElement = this.host.querySelector(
        `[data-block-id="${elementId}"]`
      ) as HTMLElement | null;
      if (blockElement) {
        blockElement.style.visibility = 'hidden';
        this._hiddenElements.add(elementId);
      }
    }

    override showElement(elementId: string): void {
      const blockElement = this.host.querySelector(
        `[data-block-id="${elementId}"]`
      ) as HTMLElement | null;
      if (blockElement) {
        blockElement.style.visibility = 'visible';
        this._hiddenElements.delete(elementId);
      }
    }

    override setDocTags(tags: string[]): void {
      if (!this.doc.meta) return;
      // Enforce maximum of two tags, deduplicated (ideally 1, but never more than 2)
      const unique = Array.from(new Set(tags));
      const clamped = unique.slice(0, 2);
      this.doc.collection.meta.setDocMeta(this.doc.id, { tags: clamped });
    }

    override addDocTag(value: string, color: string = '#37352f'): void {
      if (!this.doc.meta) return;

      // Ensure tag options structure exists
      const currentProperties = this.doc.collection.meta.properties;
      if (!currentProperties.tags) {
        this.doc.collection.meta.setProperties({
          ...currentProperties,
          tags: {
            options: [],
          },
        });
      }

      const tagOptions = this.getTagOptions();
      const existingOption = tagOptions.find(t => t.value === value);

      // Current assigned tags for this document
      const currentTags =
        this.doc.collection.meta.getDocMeta(this.doc.id)?.tags || [];

      // If already tagged with this value, nothing to do
      if (existingOption && currentTags.includes(existingOption.id)) {
        return;
      }

      // Enforce maximum of two tags per page
      if (currentTags.length >= 2) {
        return;
      }

      // Determine the tag id (use existing option if present)
      let tagId = existingOption?.id;
      if (!existingOption) {
        const id = `tag-${value.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
        this.setTagOptions([
          ...tagOptions,
          {
            id,
            value,
            color,
          },
        ]);
        tagId = id;
      }

      // Assign the tag (safe to append because we checked the limit above)
      this.doc.collection.meta.setDocMeta(this.doc.id, {
        tags: [...currentTags, tagId!],
      });
    }

    override removeDocTag(tag: string): void {
      if (!this.doc.meta) return;
      const currentTags =
        this.doc.collection.meta.getDocMeta(this.doc.id)?.tags || [];
      this.doc.collection.meta.setDocMeta(this.doc.id, {
        tags: currentTags.filter(t => t !== tag),
      });
    }

    override setTagOptions(
      options: { id: string; value: string; color: string }[]
    ): void {
      this.doc.collection.meta.setProperties({
        tags: {
          options,
        },
      });
    }

    override getTagOptions(): { id: string; value: string; color: string }[] {
      return this.doc.collection.meta.properties.tags?.options ?? [];
    }

    override async generateTitleAndTagsFromContent(
      _content: string
    ): Promise<void> {
      // TEMPORARILY DISABLED: Auto title/tags generation
      // To re-enable, use utils from './auto-title-generation'
    }
  };
}

export function createPageRootBlockSpec(
  framework: FrameworkProvider
): PageRootBlockSpecType {
  const workbenchService = framework.getOptional(
    WorkbenchService
  ) as WorkbenchService;
  return {
    ...AIPageRootBlockSpec,
    service: withAffineRootService(PageRootService, workbenchService),
    config: {
      linkedWidget: createLinkedWidgetConfig(framework),
    },
  };
}

export function createEdgelessRootBlockSpec(
  framework: FrameworkProvider
): EdgelessRootBlockSpecType {
  const workbenchService = framework.getOptional(
    WorkbenchService
  ) as WorkbenchService;
  return {
    ...AIEdgelessRootBlockSpec,
    service: withAffineRootService(
      EdgelessRootService as never,
      workbenchService
    ),
    config: {
      linkedWidget: createLinkedWidgetConfig(framework),
    },
  };
}

declare module '@blocksuite/blocks' {
  interface RootService {
    getSidebarEdgelessRootService(): Promise<EdgelessRootService | undefined>;
    setWorkbenchService(workbenchService: WorkbenchService): void;
    getWorkbenchService(): WorkbenchService | undefined;
    setKeyboardMode(mode: 'page' | 'edgeless' | null): void;
    openSidebar(): void;
    hideOthersAndAddBlock(
      flavour: string,
      props: Record<string, unknown>,
      parent?: string | BlockModel,
      parentIndex?: number
    ): string;
    hideOthersAndAddElement<T extends Record<string, unknown>>(
      type: string,
      props: T
    ): string;
    addFullViewportBlock(): string;
    hideAllElements(): void;
    selectAllElements(): void;
    selectAllBlocks(): void;
    unmounted(): void;
    hideToolbar(): void;
    showToolbar(): void;
    toggleChatPanel(visible?: boolean): void;
    currentDocTitle(): string;
    setDocTitle(title: string): void;
    setDocTitleFromString(title: string): void;
    setDocTitleFromStringNoTruncate(title: string): void;
    setViewportWithBoundAtBottom(
      bound: Bound,
      padding: [number, number, number, number],
      desiredPadding?: number,
      smooth?: boolean
    ): void;
    hideElement(elementId: string): void;
    showElement(elementId: string): void;
    askCopilotOneOff(
      prompt: string,
      options?: {
        schema?: object;
        model?: string;
        temperature?: number;
      }
    ): Promise<string>;
    askCopilotOneOffStream(
      prompt: string,
      options?: {
        schema?: object;
        model?: string;
        temperature?: number;
      }
    ): AsyncIterable<string>;
    currentDocTags(): string[];
    setDocTags(tags: string[]): void;
    addDocTag(tag: string, color?: string): void;
    removeDocTag(tag: string): void;
    setTagOptions(
      options: { id: string; value: string; color: string }[]
    ): void;
    getTagOptions(): { id: string; value: string; color: string }[];
    getAllPageContent(): Promise<PageContent>;
    getPageContentAsMarkdown(): Promise<string>;
    setFirstViewToLarge(firstViewSize?: number, secondViewSize?: number): void;
    generateTitleAndTagsFromContent(content: string): Promise<void>;
  }
}
